import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useTranslation } from '../locales/useTranslation'
import Input from '../components/shared/Input'
import Button from '../components/shared/Button'

const AuthPage = ({ showToast, onBack }) => {
  const { t } = useTranslation()
  const [isLogin, setIsLogin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [confirmedEmail, setConfirmedEmail] = useState('')
  const [form, setForm] = useState({
    email: '',
    password: '',
    naam: '',
    rol: 'rijder',
    privacyAccepted: false
  })

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleAuth = async () => {
    if (!form.email || !form.password) {
      return showToast('Vul alle verplichte velden in', true)
    }
    if (!isLogin && !form.naam) {
      return showToast('Vul je naam in', true)
    }
    if (!isLogin && !form.privacyAccepted) {
      return showToast('Ga akkoord met het privacybeleid', true)
    }

    setLoading(true)
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password
        })
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            return showToast('E-mailadres of wachtwoord is onjuist', true)
          }
          if (error.message.includes('Email not confirmed')) {
            return showToast('Bevestig eerst je e-mailadres via de mail', true)
          }
          throw error
        }
        showToast('Welkom terug! ⚡')
      } else {
        const { error, data } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: { data: { full_name: form.naam, role: form.rol } }
        })
        if (error) {
          if (error.message.includes('already registered')) {
            return showToast('Dit e-mailadres is al geregistreerd', true)
          }
          if (error.message.includes('Password should be')) {
            return showToast('Wachtwoord moet minimaal 6 tekens zijn', true)
          }
          throw error
        }
        if (data.user) {
          await supabase.from('users').update({
            privacy_accepted: true,
            privacy_accepted_at: new Date().toISOString()
          }).eq('id', data.user.id)
        }
        setConfirmedEmail(form.email)
        setShowConfirmation(true)
      }
    } catch (error) {
      showToast(error.message || t('fout'), true)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!form.email) return showToast('Vul eerst je e-mailadres in', true)
    setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
        redirectTo: `${window.location.origin}/reset-password`
      })
      if (error) throw error
      setResetSent(true)
    } catch (error) {
      showToast(error.message || t('fout'), true)
    } finally {
      setLoading(false)
    }
  }

  const BackButton = () => (
    onBack ? (
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none',
          color: 'var(--green)', fontWeight: 700,
          fontSize: 14, cursor: 'pointer',
          fontFamily: 'DM Sans, sans-serif',
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 24, padding: 0
        }}>
        ← Terug naar kaart
      </button>
    ) : null
  )

  if (showConfirmation) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--cream)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px', animation: 'fadeUp 0.5s ease'
      }}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 32 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', animation: 'pulse 2s infinite' }} />
            <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 900, fontSize: 24, color: 'var(--ink)' }}>Buurtlader</span>
          </div>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--green-pale)', border: '2px solid var(--green-mid)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36 }}>📧</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 26, fontWeight: 900, color: 'var(--ink)', marginBottom: 12 }}>Controleer je e-mail</div>
          <div style={{ color: 'var(--ink-muted)', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>We hebben een bevestigingslink gestuurd naar:</div>
          <div style={{ background: 'var(--green-pale)', border: '1px solid var(--green-mid)', borderRadius: 12, padding: '10px 16px', fontSize: 15, fontWeight: 700, color: 'var(--green)', marginBottom: 24 }}>{confirmedEmail}</div>
          <div style={{ background: 'white', borderRadius: 16, padding: 18, border: '1px solid var(--cream-dark)', marginBottom: 24, textAlign: 'left' }}>
            {[
              { step: '1', text: 'Open je e-mail inbox' },
              { step: '2', text: 'Zoek een e-mail van Buurtlader' },
              { step: '3', text: 'Klik op "Bevestig je e-mailadres"' },
              { step: '4', text: 'Je wordt automatisch ingelogd' },
            ].map(({ step, text }) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: step === '4' ? 0 : 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--green)', color: 'white', fontWeight: 900, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'Fraunces, serif' }}>{step}</div>
                <span style={{ fontSize: 14, color: 'var(--ink-soft)' }}>{text}</span>
              </div>
            ))}
          </div>
          <Button variant="secondary" fullWidth onClick={() => { setShowConfirmation(false); setIsLogin(true) }}>Terug naar inloggen</Button>
          <div style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-muted)' }}>
            E-mail niet ontvangen?{' '}
            <span onClick={handleAuth} style={{ color: 'var(--green)', fontWeight: 700, cursor: 'pointer' }}>Opnieuw versturen</span>
          </div>
        </div>
      </div>
    )
  }

  if (showForgotPassword) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <BackButton />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)' }} />
            <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 900, fontSize: 24, color: 'var(--ink)' }}>Buurtlader</span>
          </div>
          {resetSent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>📬</div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 24, fontWeight: 900, color: 'var(--ink)', marginBottom: 12 }}>E-mail verzonden!</div>
              <div style={{ color: 'var(--ink-muted)', fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>Check je inbox voor een link om je wachtwoord opnieuw in te stellen.</div>
              <Button variant="secondary" fullWidth onClick={() => { setShowForgotPassword(false); setResetSent(false) }}>Terug naar inloggen</Button>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 26, fontWeight: 900, color: 'var(--ink)', marginBottom: 6 }}>Wachtwoord vergeten?</div>
              <div style={{ color: 'var(--ink-muted)', fontSize: 14, marginBottom: 28 }}>Vul je e-mailadres in en we sturen je een reset link.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Input label={t('email')} type="email" value={form.email} onChange={v => updateForm('email', v)} placeholder={t('email_placeholder')} required />
                <Button fullWidth onClick={handleForgotPassword} disabled={loading}>{loading ? t('laden') : 'Verstuur reset link'}</Button>
                <button onClick={() => setShowForgotPassword(false)} style={{ background: 'none', border: 'none', color: 'var(--green)', fontWeight: 700, cursor: 'pointer', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>← Terug naar inloggen</button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', animation: 'fadeUp 0.5s ease' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <BackButton />
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green)', animation: 'pulse 2s infinite' }} />
            <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 900, fontSize: 24, color: 'var(--ink)' }}>Buurtlader</span>
          </div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 900, color: 'var(--ink)', marginBottom: 6 }}>{isLogin ? t('welkom_terug') : t('maak_account')}</div>
          <div style={{ color: 'var(--ink-muted)', fontSize: 14 }}>{isLogin ? t('auth_subtitle_login') : t('auth_subtitle_register')}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isLogin && <Input label={t('naam')} value={form.naam} onChange={v => updateForm('naam', v)} placeholder={t('naam_placeholder')} required />}
          <Input label={t('email')} type="email" value={form.email} onChange={v => updateForm('email', v)} placeholder={t('email_placeholder')} required />
          <div>
            <Input label={t('wachtwoord')} type="password" value={form.password} onChange={v => updateForm('password', v)} placeholder={t('wachtwoord_placeholder')} required />
            {isLogin && (
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <span onClick={() => setShowForgotPassword(true)} style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, cursor: 'pointer' }}>Wachtwoord vergeten?</span>
              </div>
            )}
          </div>
          {!isLogin && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>{t('rol_kiezen')} *</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { value: 'rijder', label: t('rijder'), emoji: '🚗' },
                  { value: 'eigenaar', label: t('eigenaar'), emoji: '🏠' },
                  { value: 'beide', label: t('beide'), emoji: '⚡' }
                ].map(role => (
                  <button key={role.value} onClick={() => updateForm('rol', role.value)} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, border: `1.5px solid ${form.rol === role.value ? 'var(--green)' : 'var(--cream-dark)'}`, background: form.rol === role.value ? 'var(--green-pale)' : 'white', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 20 }}>{role.emoji}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: form.rol === role.value ? 'var(--green)' : 'var(--ink-muted)' }}>{role.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!isLogin && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <input type="checkbox" id="privacy" checked={form.privacyAccepted} onChange={e => updateForm('privacyAccepted', e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: 'var(--green)', cursor: 'pointer', flexShrink: 0 }} />
              <label htmlFor="privacy" style={{ fontSize: 13, color: 'var(--ink-muted)', lineHeight: 1.5, cursor: 'pointer' }}>
                Ik ga akkoord met het{' '}
                <a href="https://laadlink.nl/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>privacybeleid</a>
              </label>
            </div>
          )}
          <Button fullWidth onClick={handleAuth} disabled={loading} style={{ marginTop: 8 }}>
            {loading ? t('laden') : isLogin ? t('inloggen') : t('registreren')}
          </Button>
          <div style={{ textAlign: 'center', color: 'var(--ink-muted)', fontSize: 14, marginTop: 4 }}>
            {isLogin ? t('geen_account') : t('al_account')}{' '}
            <span onClick={() => setIsLogin(!isLogin)} style={{ color: 'var(--green)', fontWeight: 700, cursor: 'pointer' }}>{isLogin ? t('registreer_hier') : t('login_hier')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthPage
